import React, {useId, useMemo} from 'react'
import {Box, Flex, Text, AspectRatio, useSlotRecipe} from '@chakra-ui/react3'
import PropTypes from 'prop-types'
import Slider from '../../components/cms/slider'
import ModuleContainer from '../../components/cms/module-container'
import Asset from '../../components/cms/asset'
import {galleryRecipe, slideRecipe} from './index.recipe'
import Heading from '../../components/cms/heading'
import Copy from '../../components/cms/copy'
import {hasValidString, isVideoAsset} from '../../utils/utils'

const Slide = ({darken, asset, headline, hideHeadline, copy, sliderId, headingLevel, index}) => {
    const videoRef = React.useRef(null)

    const recipe = useSlotRecipe({recipe: slideRecipe})
    const styles = useMemo(() => recipe({darken: darken.replace('%', '')}), [recipe, darken])

    return (
        <Flex {...styles.root} data-is-video={asset.type !== 'image'}>
            <Box {...styles.textContainer}>
                {headline && (
                    <Text
                        as={`h${headingLevel + 1}`}
                        id={sliderId + '-headline-' + index}
                        className={hideHeadline ? 'sr-only' : undefined}
                        {...styles.headline}
                    >
                        {headline}
                    </Text>
                )}
                {copy?.text && <Copy alignment="inherit" variant="gallery" {...copy} />}
            </Box>

            <Box position="relative">
                {/*top layer*/}
                {headline && <Box {...styles.topLayer}></Box>}
                {/*bottom layer*/}
                {!isVideoAsset(asset) && <Box {...styles.bottomLayer}></Box>}
                <AspectRatio ratio={{base: 1, s: 2}}>
                    <Asset
                        asset={asset}
                        videoRef={videoRef}
                        objectFit="cover"
                        widths={{base: '100vw', s: '88vw', l: 1680}}
                        aspectRatio={{base: 1, s: 2}}
                        variant="gallery"
                    />
                </AspectRatio>
            </Box>
        </Flex>
    )
}

Slide.propTypes = {
    darken: PropTypes.string,
    asset: PropTypes.object,
    headline: PropTypes.string,
    copy: PropTypes.object,
    sliderId: PropTypes.string,
    hideHeadline: PropTypes.bool,
    headingLevel: PropTypes.number,
    index: PropTypes.number
}

function Gallery({
    moduleId,
    moduleType,
    pageId,
    headingLevel,
    heading,
    copy,
    galleryItems,
    anchor,
    background,
    spacing
}) {
    const sliderId = useId()
    const recipe = useSlotRecipe({recipe: galleryRecipe})
    const styles = useMemo(() => recipe(), [recipe])

    const ariaLabel = useMemo(
        () =>
            [
                hasValidString(heading.subline) && `${heading.subline} -`,
                heading.headline1,
                heading.headline2
            ]
                .filter(hasValidString)
                .join(' '),
        [heading.subline, heading.headline1, heading.headline2]
    )

    return (
        <ModuleContainer
            moduleId={moduleId}
            moduleType={moduleType}
            pageId={pageId}
            anchor={anchor}
            background={background}
            spacing={spacing}
            {...styles.moduleContainer}
            heading={heading}
        >
            <Flex {...styles.textContainer}>
                <Heading {...heading} headingLevel={headingLevel} alignment="inherit"></Heading>
                <Copy alignment="inherit" {...copy} />
            </Flex>

            <Box pt={{base: 'spacing-40', s: 'spacing-80'}}>
                <Slider
                    itemsPerSlide={1}
                    variant="gallery"
                    sliderId={sliderId}
                    sliderLabel={ariaLabel}
                >
                    {galleryItems.map((item, index) => (
                        <Slide
                            {...item}
                            key={index}
                            index={index}
                            sliderId={sliderId}
                            headingLevel={headingLevel}
                        ></Slide>
                    ))}
                </Slider>
            </Box>
        </ModuleContainer>
    )
}

Gallery.propTypes = {
    alignment: PropTypes.string,
    headingLevel: PropTypes.number,
    heading: PropTypes.object,
    copy: PropTypes.object,
    galleryItems: PropTypes.array,
    anchor: PropTypes.string,
    background: PropTypes.string,
    moduleId: PropTypes.string,
    moduleType: PropTypes.string,
    pageId: PropTypes.string,
    spacing: PropTypes.oneOf(['default', 'reduced', 'min', 'min-top', 'min-bottom'])
}

export default Gallery
